import Vue from 'vue';
import { getTimestamp, findDependants } from './helpers';
import { logAsDelivered } from '@assessment/../api/log';
import { postAnswer, postLegacyWrittenAnswer } from '@assessment/../api/answer';

export default {
  namespaced: true,

  state: {
    emails: [],
    deliveredEmails: [],
  },

  actions: {
    DELIVER_EMAILS: async ({ rootGetters, commit }, emailsToDeliver) => {
      if (rootGetters['assessmentType'].isWebApp) {
        const emailsToDeliverKeys = emailsToDeliver.map((email) => {
          return {
            questionKey: email.questionKey,
            isAutoDelivered: email.isAutoDelivered ? email.isAutoDelivered : 0,
          };
        });

        await logAsDelivered({ deliveredKeys: emailsToDeliverKeys, logActionTypeKey: 13 });
      }
      // Timestamp logic
      emailsToDeliver.forEach((email) => {
        const hasNoDeps = email.dependsOn == null && email.dependencies.length === 0;
        const hasNoTimer = email.timer === 0 || email.timer === null;

        if (!hasNoDeps || !hasNoTimer) {
          email.timestamp = getTimestamp(
            rootGetters.showTimerAsClock,
            rootGetters['time/clockTimer']
          );
        }

        if (email.isWrittenResponse) email['writtenResponse'] = '';
        email.isDelivered = true;
      });

      commit('DELIVER_EMAILS', emailsToDeliver);
    },

    ANSWER_EMAIL: async (
      { commit, rootGetters, getters, rootState },
      { questionkey, answerkey, isSmartThreading, writtenResponse }
    ) => {
      if (rootState['assessmentTypeKey'] === 1) {
        await postAnswer({ answerkey, questionkey, isSmartThreading, writtenResponse });
      }
      const answers = typeof answerkey == 'object' ? answerkey : [answerkey];
      const dependantEmails = findDependants(getters.threadEmails, answers);
      const dependantMessages = findDependants(rootGetters['chat/messagesFromThread'], answers);

      commit('SET_DEPENDANT', {
        dependantEmails,
        rootGetters,
        time: { minutes: rootState.time.minutes, hour: rootState.time.hour },
      });
      commit('chat/SET_DEPENDANT', { dependantMessages, rootGetters }, { root: true });
      commit('ANSWER_EMAIL', { questionkey, answerkey, isSmartThreading, writtenResponse });
    },

    ANSWER_EMAIL_WRITTEN: async ({ commit, rootState }, { questionkey, writtenResponse }) => {
      const payload = { questionkey, writtenResponse };
      if (rootState['assessmentTypeKey'] === 1) {
        await postLegacyWrittenAnswer(payload);
      }
      commit('ANSWER_EMAIL_WRITTEN', payload);
    },

    UPDATE_EMAIL: ({ state, commit, rootState }, { questionkey, oldAnswerkey, newAnswerkey }) => {
      commit('ANSWER_EMAIL', { questionkey, answerkey: newAnswerkey });
      if (rootState['assessmentTypeKey'] == 1) {
        return new Promise(function (resolve, reject) {
          const payload = { questionkey, oldAnswerkey, newAnswerkey };
          $.ajax({
            url: '/capsiminbox/webapp/update',
            method: 'POST',
            dataType: 'json',
            contentType: 'application/json;charset=utf-8',
            data: JSON.stringify(payload),
          })
            .then(() => {
              commit('ANSWER_EMAIL', { questionkey, answerkey: newAnswerkey });
              resolve();
            })
            .catch((err) => reject(err));
        });
      }
    },

    READ_EMAIL: ({ commit, rootState }, { objectKey, isRead }) => {
      if (isRead) return;

      if (rootState['assessmentTypeKey'] == 1) {
        const payload = { actionKey: 5, objectKey };

        $.post({ url: '/capsiminbox/webapp/log-activity', dataType: 'json', data: payload })
          .done(() => commit('READ_EMAIL', objectKey))
          .fail((err) => console.log(err));
      }
    },

    TOGGLE_FLAG: ({ commit, assessmentTypeKey, rootState }, { objectKey, activityValue }) => {
      if (rootState['assessmentTypeKey'] == 1) {
        const payload = { actionKey: 7, objectKey, activityValue };

        $.post({ url: '/capsiminbox/webapp/log-activity', dataType: 'json', data: payload })
          .done(() => commit('TOGGLE_FLAG', { id: objectKey, value: activityValue }))
          .fail((err) => console.log(err));
      } else {
        commit('TOGGLE_FLAG', { id: objectKey, value: activityValue });
      }
    },
  },

  mutations: {
    DELIVER_EMAILS: (state, emails) => {
      state.deliveredEmails.push(...emails);
    },

    EXPEDITE_EMAIL: (state, { email, time }) => {
      // TODO: Refactor this so this retrieves the email from the module state
      Vue.set(email, 'isAutoDelivered', true);
      Vue.set(email, 'timer', time);
    },

    ANSWER_EMAIL: (state, { questionkey, answerkey, writtenResponse }) => {
      const question = state.emails.find((question) => question.questionKey === questionkey);
      let answers = typeof answerkey == 'object' ? answerkey : [answerkey];
      let answer = question.answers.filter((answer) => answers.includes(answer.answerKey));

      if (question.FK_questionTypeKey === 3) {
        Vue.set(question, 'writtenResponse', writtenResponse);
      }

      Vue.set(question, 'answer', answer);

      if (question.isSmartThreading != 1) {
        Vue.set(question, 'isSent', true);
      } else {
        Vue.set(question, 'isActive', false);
        if (question.smartThreadingSelectedAnswers) {
          let temp = question.smartThreadingSelectedAnswers;
          temp.push(answerkey);
          Vue.set(question, 'smartThreadingSelectedAnswers', temp);
        } else {
          Vue.set(question, 'smartThreadingSelectedAnswers', [answerkey]);
        }
      }
    },

    ANSWER_EMAIL_WRITTEN: (state, { questionkey, writtenResponse }) => {
      const question = state.emails.find((question) => question.questionKey === questionkey);
      //Set answer property in question and isSent to true
      Vue.set(question, 'writtenResponse', writtenResponse);
      Vue.set(question, 'isSent', true);
    },

    SET_DEPENDANT: (state, { dependantEmails, rootGetters, time }) => {
      const firstSequence = Math.min.apply(
        Math,
        state.emails.map((o) => o.sequence)
      );

      dependantEmails.forEach((e) => {
        if (e.isSmartThreading == 0) {
          Vue.set(e, 'timer', e.timer + rootGetters['time/elapsed']);
          Vue.set(e, 'dependencies', []);
        } else {
          e.timestamp = getTimestamp(rootGetters.showTimerAsClock, rootGetters['time/clockTimer']);
          Vue.set(e, 'isActive', true);
        }
        Vue.set(e, 'sequence', firstSequence - 1);
      });
    },

    READ_EMAIL: (state, id) =>
      (state.emails.find((email) => email.questionKey === id).isRead = true),

    SET_EMAILS: (state, emails) => Vue.set(state, 'emails', emails),

    TOGGLE_FLAG: (state, { id, value }) =>
      (state.emails.find((email) => email.questionKey === id).isFlagged = Boolean(value)),

    SET_WRITTEN_REPONSE: (state, { questionKey, writtenResponse }) => {
      let email = state.emails.find((email) => email.questionKey === questionKey);
      if (email.hasOwnProperty('writtenResponse')) email.writtenResponse = writtenResponse;
      else Vue.set(email, 'writtenResponse', writtenResponse);
    },
  },

  getters: {
    activeEmails: (state, getters, rootState) => {
      return getters.sortedEmails.filter((email) => {
        let showThreadedEmail = false;

        // This fail safe feature needs be reworked, recentlySelectedAnswerKey needs to be replaced with something else
        if (email.isSmartThreading == 1 && email.failsafe > 0) {
          //If # of answers = # of answers picked on corresponding email, then show this failsafe question
          let correspondingEmail = state.emails.filter((e) => e.questionKey == email.failsafe);
          let correspondingAnswers = correspondingEmail[0].answers
            ? correspondingEmail[0].answers.length
            : 0;
          let correspondingEmailAnsweresAnsweredCount = 0;

          if (correspondingEmail[0].smartThreadingSelectedAnswers)
            correspondingEmailAnsweresAnsweredCount =
              correspondingEmail[0].smartThreadingSelectedAnswers.length;

          if (
            correspondingEmailAnsweresAnsweredCount >= correspondingAnswers &&
            email.dependencies.some((d) => d.FK_answerKey == rootState.recentlySelectedAnswerKey)
          ) {
            showThreadedEmail = true;
          }
        }

        let count = 0;
        if (email.smartThreadingSelectedAnswers) count = email.smartThreadingSelectedAnswers.length;
        let answers = email.answers.length;

        if (email.isSmartThreading == 1 && email.isActive == true && !email.failsafe)
          showThreadedEmail = true;
        if (email.isSmartThreading == 1 && count >= answers) showThreadedEmail = false;

        return (email.isSmartThreading != 1 && email.dependencies.length == 0) || showThreadedEmail;
      });
    },

    answeredEmails: (state, getters) => getters.activeEmails.filter((email) => email.isSent),

    cleanEmails: (state, getters) => getters.activeEmails.filter((email) => !email.isSent),

    emailsCompleted: (state, getters) => {
      const emailsWithAnswers = getters.activeEmails.filter(
        (e) => e.answers.length > 0 || e.isWrittenResponse == 1
      );
      return emailsWithAnswers.every(
        (e) =>
          ((e.answer !== undefined && e.isWrittenResponse == 0 && e.isSmartThreading != 1) ||
            (e.isSent && e.isWrittenResponse == 1)) &&
          e.isDelivered
      );
    },

    flaggedEmails: (state, getters) =>
      getters.activeEmails.filter((email) => email.isFlagged === true),

    sortedEmails: (state) =>
      state.emails.sort((a, b) => {
        const aSeq = a.sequence;
        const bSeq = b.sequence;

        return aSeq > bSeq ? 1 : aSeq < bSeq ? -1 : 0;
      }),

    threadEmails: (state) => state.emails.filter((email) => email.dependencies.length > 0),

    unansweredEmails: (state, getters) =>
      getters.cleanEmails.filter((email) => email.answers.length > 0),

    firstEmail: (state) =>
      state.emails.find(
        (email) =>
          email.sequence ===
          Math.min.apply(
            Math,
            state.emails.map((o) => o.sequence)
          )
      ),

    unreadEmails: (state, getters) =>
      getters.activeEmails.filter((email) => email.isRead === false),
    deliveredEmails: (state) => state.deliveredEmails,
  },
};
